import React from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiCalendar, FiStar, FiEdit2, FiCheckCircle, FiTrash2 } = FiIcons;

const TeamMemberCard = ({ member, onEdit, onDelete, canEdit }) => {
  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-100 text-purple-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      case 'team_member': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'manager': return 'Manager';
      case 'team_member': return 'Team Member';
      default: return 'Unknown';
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    // Invoke the delete callback provided by parent
    if (onDelete) {
      onDelete();
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit();
    }
  };

  return (
    <motion.div
      whileHover={{ y: -2 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all relative"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <img 
            src={member.avatar || 'https://via.placeholder.com/150'} 
            alt={member.name} 
            className="w-12 h-12 rounded-full object-cover bg-gray-100" 
          />
          <div>
            <h3 className="font-semibold text-gray-900">{member.name}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(member.role)}`}>
              {getRoleName(member.role)}
            </span>
          </div>
        </div>
        
        {canEdit && (
          <div className="flex space-x-1">
            <button
              onClick={handleEditClick}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Edit Member"
            >
              <SafeIcon icon={FiEdit2} className="w-4 h-4" />
            </button>
            <button
              onClick={handleDeleteClick}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete Member"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center space-x-2 text-gray-600">
          <SafeIcon icon={FiMail} className="w-4 h-4" />
          <span className="text-sm truncate">{member.email}</span>
        </div>
        <div className="flex items-center space-x-2 text-gray-600">
          <SafeIcon icon={FiCalendar} className="w-4 h-4" />
          <span className="text-sm">
            Joined {member.joinDate ? formatDistanceToNow(new Date(member.joinDate), { addSuffix: true }) : 'Recently'}
          </span>
        </div>

        <div className="pt-3 border-t border-gray-100">
          <p className="text-sm font-medium text-gray-900 mb-2">Department</p>
          <p className="text-sm text-gray-600">{member.department || 'Not Assigned'}</p>
        </div>

        {member.performance && (
          <div className="pt-3 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-900 mb-3">Performance</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Score</span>
                <span className="text-sm font-medium text-gray-900">
                  {member.performance.score}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${member.performance.score}%` }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="bg-blue-600 h-2 rounded-full"
                />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="flex items-center space-x-1">
                  <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-gray-600">
                    {member.performance.tasksCompleted} tasks
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-500" />
                  <span className="text-xs text-gray-600">
                    {member.performance.avgRating} rating
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default TeamMemberCard;